/**
 * Lets a page tell the TopBar "for URL segment X, render label Y".
 * The TopBar's breadcrumb naturally shows raw URL segments — for
 * detail pages (e.g. /customers/:id) that means a raw uuid unless we
 * override it. Pages register an override on mount, clean up on
 * unmount.
 */

import { useEffect } from 'react'
import { create } from 'zustand'

interface CrumbOverrideState {
  overrides: Record<string, string>
  set: (segment: string, label: string) => void
  clear: (segment: string) => void
}

export const useBreadcrumbStore = create<CrumbOverrideState>((set) => ({
  overrides: {},
  set: (segment, label) =>
    set((s) => ({ overrides: { ...s.overrides, [segment]: label } })),
  clear: (segment) =>
    set((s) => {
      const next = { ...s.overrides }
      delete next[segment]
      return { overrides: next }
    }),
}))

export function useBreadcrumbLabel(
  segment: string | undefined,
  label: string | undefined,
) {
  const set = useBreadcrumbStore((s) => s.set)
  const clear = useBreadcrumbStore((s) => s.clear)
  useEffect(() => {
    if (!segment || !label) return
    set(segment, label)
    return () => clear(segment)
  }, [segment, label, set, clear])
}
